/**
 * Form to invite members to community
 *
 */

/**
 * React
 */
import * as React from 'react';

/**
 * React form
 */
import { Field, reduxForm } from 'redux-form';

/**
 * Fabric UI
 */
import {
  ActionButton,
  PrimaryButton,
  DefaultButton
} from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { MessageBar } from '@fluentui/react/lib/MessageBar';
import { Dialog, DialogFooter } from '@fluentui/react/lib/Dialog';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';

/**
 * React form field
 */
import TextField from '../FormFields/TextField';
import {FormEventHandler} from 'react';

interface IProps {
  communityId: string | number;
  isSubmitted: boolean;
  isProtected?: boolean;
  initialValues: any;
  constants: any;

  requestFetchCommunity(communityId: string | number): void;
  requestSaveMembers(communityId: string | number, flag: boolean): void;
  endEditInviteMembers(communityId: string | number): void;
  toggleBulkInvite(event: React.MouseEvent<any>): void;

  initialize: any;
  handleSubmit(func: any): FormEventHandler;
  pristine: boolean;
  submitting: boolean;
}

const InviteMembersBulkComponent = (props) => {
  const {
    communityId,
    isSubmitted,
    initialValues,
    constants,

    requestFetchCommunity,
    requestSaveMembers,
    endEditInviteMembers,
    toggleBulkInvite,

    reset,
    initialize,
    handleSubmit,
    pristine,
    submitting,
  } = props;

  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [selectedKey, setSelectedKey] = React.useState<string>('Community');

  const onSubmit = () => {
    requestSaveMembers(communityId, true);
  };

  const onClose = (event) => {
    /**
     * Check if form is submitted and if alert is showing
     */
    if (!isSubmitted && !showAlert) {
      setShowAlert(true);
    } else {
      endEditInviteMembers(communityId);
    }
  };

  const onClear = (event) => {
    reset();
  };

  const parse = (value, name) => {
    // Choose text separator based on the one that appears first in the text.
    const scPos = value.indexOf(";");
    const crPos = value.indexOf("\r");
    const nlPos = value.indexOf("\n");
    // By default use semicolon as separator if one is not already supplied
    let separator = ";";

    // If cr or nl appears before first semicolon, use that instead.
    if (crPos > -1 || nlPos > -1) {
      if (scPos === -1 || (crPos > -1 && scPos > crPos) || (nlPos > -1 && scPos > nlPos)) {
        separator = "\r\n";
      }
    }

    value = value.replace(/(\r\n|\n|\r)/gm, ";");
    const outlookRegEx = /[^;]+<([a-zA-Z0-9+._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)>;?/;
    let emailAddress = outlookRegEx.exec(value);
    while (emailAddress && emailAddress[0]) {
      value = value.replace(emailAddress[0], emailAddress[1] + ';');
      emailAddress = outlookRegEx.exec(value);
    }
    if (separator !== ';') {
      value = value.replace(/;/g, separator);
    }
    return value;
  };

  const  onDismissAlert = (event) => {
    setShowAlert(false);
  };

  const  handleLinkClick = (item) => {
    setSelectedKey('Community');
  };

  const  renderItemLink = (link, defaultRenderer) => {
    return (
      <span>
        {defaultRenderer(link)}
        <Icon iconName='ChevronRight' />
      </span>
    );
  };

  // React.useEffect( () => {
  //   initialize(initialValues);
  // }, [initialValues]);

  React.useEffect( () => {
    if ( !initialValues || (initialValues && initialValues.communityId !== communityId) ) {
      requestFetchCommunity(communityId);
    } else {
      initialize(initialValues);
    }
  }, [communityId, initialValues]);

  const _handleGoBack = () => toggleBulkInvite();

  return (
    <React.Fragment>
      {initialValues && (
        <div>
          {!isSubmitted && (
            <ActionButton
              className='govTeams-actionButton'
              onClick={_handleGoBack}
              iconProps={{ iconName: 'ChevronLeft' }}
              styles={{
                root: {
                  marginTop: -30
                }
              }}
            >
              Back to people picker
            </ActionButton>
          )}
          <div className='formGrid formBasic'>
            <Pivot
              selectedKey={isSubmitted ? 'Summary' : selectedKey}
              onLinkClick={handleLinkClick}
            >
              <PivotItem
                headerText='Community'
                itemKey='Community'
                onRenderItemLink={renderItemLink}
              >
                <h1>
                  Invite members and guests to {initialValues.displayName}
                </h1>
                <hr />
                <div
                  dangerouslySetInnerHTML={{
                    __html: constants.TEXT_INVITEMEMBERS_BULK
                  }}
                />
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className='formFields'>
                    <div className='formField'>
                      <Field
                        aria-labelledby='members-email'
                        name='members'
                        component={TextField}
                        placeholder="Enter members' email"
                        multiline={true}
                        parse={parse}
                        rows='10'
                      />
                    </div>
                  </div>
                  <div className='formFooter'>
                    <DefaultButton
                      text='Clear'
                      onClick={onClear}
                      styles={{ root: { marginRight: 10 } }}
                    />
                    <PrimaryButton
                      text='Send invite'
                      type='submit'
                      disabled={pristine || submitting}
                      className='buttonSpacing'
                    />
                  </div>
                  <MessageBar>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: constants.TEXT_INVITEMEMBERS_INFO
                      }}
                    />
                  </MessageBar>
                </form>
              </PivotItem>
              <PivotItem headerText='Summary' itemKey='Summary'>
                {<React.Fragment>
                  <h1 className='heading-success'>
                    {constants.TEXT_INVITEMEMBERS_SUCCESSTITLE}
                  </h1>
                  <div
                    className='spacing-bottom'
                    dangerouslySetInnerHTML={{
                      __html: constants.TEXT_INVITEMEMBERSBULK_SUCCESSTEXT
                    }}
                  />
                </React.Fragment>
                }
                <PrimaryButton
                  text='Close'
                  onClick={onClose}
                />
              </PivotItem>
            </Pivot>
          </div>
          <Dialog
            dialogContentProps={{
              title: constants.TEXT_INVITEMEMBERS_ALERTTITLE,
              showCloseButton: true
            }}
            modalProps={{
              isBlocking: true,
              isDarkOverlay: true,
              className: 'govTeams-dialog'
            }}
            hidden={!showAlert}
            onDismiss={onDismissAlert}
          >
            <p>{constants.TEXT_INVITEMEMBERS_ALERTTEXT}</p>
            <DialogFooter>
              <DefaultButton onClick={onDismissAlert}>
                Cancel
              </DefaultButton>{' '}
              <PrimaryButton onClick={onClose}>Continue</PrimaryButton>
            </DialogFooter>
          </Dialog>
        </div>
      )}
    </React.Fragment>
  );
}

// export class InviteMembersBulkFormComponent extends React.Component<any, any> {
//
//   public async componentDidMount() {
//     const { communityId } = this.props;
//
//     if (!this.props.initialValues) {
//       await this.props.communityActionCreators.requestFetchCommunity(communityId);
//     } else {
//       this.props.initialize(this.props.initialValues);
//     }
//   }
//
//   public componentDidUpdate(prevProps) {
//     if (!prevProps.initialValues && this.props.initialValues) {
//       this.props.initialize(this.props.initialValues);
//     }
//   }
// }
//
// export const InviteMembers = reduxForm({
//   form: 'InviteMembersForm'
// })(InviteMembersBulkFormComponent);

export default reduxForm({
  form: 'InviteMembersForm'
})(InviteMembersBulkComponent);
